/* eslint-disable max-lines */

import { clearTree, requestTreeFiltersToogle } from 'actions';
import { allowedReadOnlyDimensions, metricsLinksMap } from 'constants/dashboard';

import { SHIELD_ICON } from 'constants/envelopeIcons';

import { resourceQueryParamName } from 'constants/resourceQueryNames';
import withWidget from 'decorators/widget';
import WidgetEmptyState from 'decorators/widget/WidgetEmptyState';
import WidgetError from 'decorators/widget/WidgetError';
import WidgetLoading from 'decorators/widget/WidgetLoading';
import React from 'react';
import { useDispatch } from 'react-redux';
import { getCustomerDomain, getUser } from 'selectors/auth';
import { getNavParamsByResource, getNavWidgetFilters } from 'selectors/nav';
import { getPermissionsPolicy } from 'selectors/permissions';
import { useSelector } from 'store';
import { DashboardMetric, DefaultWidgetComponentProps } from 'types';
import logEvent from 'utils/analytics';
import { buildSimpleDataFromMetrics } from 'utils/dashboard';
import { getValuesFromDimensions, transformToString } from 'utils/parserTree';
import { isActionAuthorized } from 'utils/permissions';
import { useHistory } from 'utils/urls';
import ExploreButton from '../../ReviewSets/ExploreButton';

export const HigherRiskResolvedWidget: React.FC<DefaultWidgetComponentProps> = (props) => {
  const { widgetData, queryData, widgetId } = props;

  const history = useHistory();
  const user = useSelector(getUser);
  const dispatch = useDispatch();
  const policy = useSelector(getPermissionsPolicy);
  const customerDomain = useSelector(getCustomerDomain);
  const widgetFilters = useSelector(getNavWidgetFilters(widgetId));
  const filters = useSelector(getNavParamsByResource(resourceQueryParamName.metrics));

  if (widgetData == null || queryData == null) {
    return <WidgetLoading />;
  }

  const processedData = buildSimpleDataFromMetrics(widgetData, queryData, true);

  if ('error' in processedData && processedData.error != null) {
    return <WidgetError msg={processedData.error} />;
  }

  if ('error' in processedData) {
    return <WidgetError msg={processedData.error} />;
  }

  if ('data' in processedData && processedData.data.length === 0) {
    return <WidgetEmptyState />;
  }

  const handleClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent> | React.MouseEvent<SVGGElement, MouseEvent>,
    query: DashboardMetric
  ): void => {
    e.stopPropagation();
    const [id, type] = widgetId.split('--');
    logEvent(`Dashboard-click-${id}-${type}`);

    if (query == null) {
      return;
    }

    const info = metricsLinksMap[query.metric];
    if (info == null || !isActionAuthorized(info.action, user.roles, policy)) {
      return;
    }

    const dimensions: Record<string, string | string[]> = {};
    const infoDimension = info.dimensions;
    if (infoDimension != null) {
      // Top level dimensions
      Object.entries(infoDimension).forEach(([dimension, param]) => {
        const allowed = allowedReadOnlyDimensions[query.metric];

        // if allowedDimension is not defined or the dimension is not included,
        // just ignore it
        if (allowed == null || !allowed.includes(dimension)) {
          return;
        }

        let value: string | string[] = '';
        if (filters[dimension] != null) {
          const topValue = filters[dimension];
          value = topValue;
        }

        if (widgetFilters[dimension] != null) {
          const widgetValue = filters[dimension];
          value = widgetValue;
        }

        dimensions[`${info.resource}__${param}`] = value;
      });

      // Metric level dimensions
      if (query.dimensions != null) {
        query.dimensions.forEach((dimension) => {
          const allowed = allowedReadOnlyDimensions[query.metric];

          // if allowedDimension is not defined or the dimension is not included,
          // just ignore it
          if (allowed == null || !allowed.includes(dimension.name)) {
            return;
          }

          if (!dimensions[`${info.resource}__${infoDimension[dimension.name]}`]) {
            dimensions[`${info.resource}__${infoDimension[dimension.name]}`] = [dimension.value];
          } else if (
            !dimensions[`${info.resource}__${infoDimension[dimension.name]}`].includes(
              dimension.value
            )
          ) {
            dimensions[`${info.resource}__${infoDimension[dimension.name]}`] = [
              ...dimensions[`${info.resource}__${infoDimension[dimension.name]}`],
              dimension.value,
            ];
          }
        });
      }
    }

    if (query.metric.includes('platform_')) {
      if (query.metric === 'platform_a') {
        // TODO: use customer config to get these values for platform a and b
        dimensions[`${info.resource}__platforms`] = 'o365';
      }

      if (query.metric === 'platform_b') {
        dimensions[`${info.resource}__platforms`] = 'o365_teams';
      }
    }

    const tree = getValuesFromDimensions(dimensions);

    dispatch(
      requestTreeFiltersToogle({
        value: true,
      })
    );
    dispatch(clearTree());
    history.pushLookup({
      customerDomain,
      routeName: info.route,
      queryParams: {
        [`${info.resource}__filters_search`]: transformToString(tree),
      },
    });
  };

  return (
    <div className="h-full w-full flex items-end">
      <div className="flex flex-col gap-2 w-full text-menu-item overflow-visible">
        <div className="w-full rounded border border-litlingo-gray-2 text-body">
          <div className="flex flex-row border-b border-litlingo-gray-2">
            <div className="px-10 py-7 flex flex-row gap-4 flex-1 bg-litlingo-gray-0.5">
              <div className="w-12 text-litlingo-success">{SHIELD_ICON}</div>
              <div className="flex flex-col gap-2 justify-center">
                <span className="text-heading-3">My Escalated Messages</span>
                <span className="underline">{`Contains ${processedData.data[0].count} messages`}</span>
              </div>
            </div>
            <div className="px-2 flex items-center justify-center">
              <ExploreButton
                onClick={(e): void => {
                  handleClick(e, queryData.queries[0]);
                }}
                text="View"
              />
            </div>
          </div>
          <div className="flex flex-row justify-between text-body">
            <button
              type="button"
              className="pt-3 pb-2.5 flex justify-center flex-1"
              onClick={(e): void => handleClick(e, queryData.queries[1])}
            >
              {`With Legal: ${processedData.data[1].count}`}
            </button>
            <div className="mt-2.5 mb-2 border-l border-litlingo-gray-2" />
            <button
              type="button"
              className="pt-3 pb-2.5 flex justify-center flex-1"
              onClick={(e): void => handleClick(e, queryData.queries[2])}
            >
              {`Acceptable: ${processedData.data[2].count}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default withWidget({
  bgClassColor: 'bg-transparent',
  overrideTimeRange: '-20years',
})(HigherRiskResolvedWidget);
